// Nova shop UI, loaded on every page from sections/header.liquid.
//
//   1. Wishlist: any [data-wishlist-toggle] button (the heart on product cards,
//      "Add to wishlist" under the product page's Add to cart) saves the product
//      to this device. The heart in the header opens a side panel listing them.
//   2. Quick add: form[data-quick-add] on product cards posts to /cart/add.js and
//      updates the header badge without leaving the page.
//   3. Product cards: colour dots painted from the option value names.
//   4. Cart page: - / + buttons around each quantity box, applied instantly.
//   5. Rails (v25): round previous / next arrows beside the heading of every
//      sideways-scrolling row, for desktop visitors with a mouse.
//
// The wishlist lives in localStorage on purpose: no app, no account, nothing to
// pay for. The cost is that it stays on the device it was made on; the panel
// says so. Strings come from window.novaShopText, set in the header.

(() => {
  if (window.novaShop) return;
  window.novaShop = true;

  const KEY = "nova-wishlist";
  const T = Object.assign(
    {
      add: "Add to wishlist", saved: "Saved to wishlist", title: "Wishlist",
      savedToast: "Saved to your wishlist", removedToast: "Removed from your wishlist",
      remove: "Remove", empty: "Nothing saved yet.", deviceNote: "", close: "Close",
      addToCart: "Add to cart", choose: "Choose options", added: "Added to cart",
      priceTbc: "Price to be confirmed", colours: "colours",
      decrease: "Decrease quantity", increase: "Increase quantity",
    },
    window.novaShopText || {},
  );

  // --- colour names -> swatch fills (same table as snippets/buy-box.liquid) ---
  const COLOURS = {
    black: "#141414", white: "#f4f4f1", red: "#d62f2f", blue: "#2563d8", navy: "#1c2c52",
    green: "#1f9d55", pink: "#f06ea9", purple: "#7b3fc6", yellow: "#f7d417", "neon yellow": "#e5ff1f",
    orange: "#f47b20", gold: "#c9a23a", grey: "#8a8a8f", gray: "#8a8a8f", silver: "#c3c3c8",
    beige: "#d8c29b", tan: "#b8834f", brown: "#6b4327", turquoise: "#1fc2c0", maroon: "#6d1a24",
    natural: "#c79f72", "natural leather": "#c79f72",
    chocolate: "#5a3a22", "dark blue": "#1b2f6b", "dark grey": "#4a4a4f", "dark gray": "#4a4a4f",
    "light blue": "#7fb8f0", "sky blue": "#5bb3ee", "light grey": "#c9c9cc", "light gray": "#c9c9cc",
    "dark green": "#1d5c3a", cream: "#efe6d2",
  };
  const MIX = "conic-gradient(#d62f2f 0 25%, #2563d8 0 50%, #f7d417 0 75%, #1f9d55 0)";
  const PATTERNS = {
    floral: "conic-gradient(#f06ea9 0 25%, #f7d417 0 50%, #1f9d55 0 75%, #7b3fc6 0)",
    "tropical print": "conic-gradient(#1f9d55 0 30%, #f47b20 0 55%, #1fc2c0 0 80%, #f7d417 0)",
    camo: "conic-gradient(#4b5320 0 22%, #8a7f5a 0 47%, #2f3a1f 0 71%, #6f6b46 0)",
    "mixed colours": MIX, "mixed colors": MIX, "mix colours": MIX, "mix colors": MIX,
  };
  function swatchFor(value) {
    const key = String(value || "").trim().toLowerCase();
    if (PATTERNS[key]) return PATTERNS[key];
    if (COLOURS[key]) return COLOURS[key];
    const fills = key.split(/\s*(?:&|\/|,|\+|\band\b)\s*/).filter(Boolean).map((p) => COLOURS[p]);
    if (!fills.length || fills.some((f) => !f)) return null;
    if (fills.length === 1) return fills[0];
    if (fills.length === 2) return `linear-gradient(135deg, ${fills[0]} 50%, ${fills[1]} 50%)`;
    const step = 100 / fills.length;
    return `conic-gradient(${fills.map((f, i) => `${f} ${i * step}% ${(i + 1) * step}%`).join(", ")})`;
  }

  // --- storage ------------------------------------------------------------------
  function read() {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || "[]");
      return Array.isArray(list) ? list.filter((i) => i && i.handle) : [];
    } catch (error) {
      return [];
    }
  }
  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, 60)));
    } catch (error) {
      /* private mode: the heart still toggles for this page view */
    }
  }
  const safeUrl = (u) => (/^\/(?!\/)/.test(u || "") ? u : "#");
  const safeImg = (u) => (/^(https?:)?\/\//.test(u || "") ? u : "");

  // --- toast --------------------------------------------------------------------
  let toastEl = null;
  let toastTimer = 0;
  function toast(text) {
    if (!text) return;
    if (!toastEl) {
      toastEl = document.createElement("p");
      toastEl.className = "ns-toast";
      toastEl.setAttribute("role", "status");
      toastEl.setAttribute("aria-live", "polite");
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = text;
    toastEl.classList.add("is-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove("is-on"), 2000);
  }

  // --- wishlist -----------------------------------------------------------------
  function toggle(button) {
    const d = button.dataset;
    if (!d.handle) return;
    let list = read();
    const on = list.some((i) => i.handle === d.handle);
    if (on) {
      list = list.filter((i) => i.handle !== d.handle);
    } else {
      list.unshift({
        handle: d.handle, title: d.title || "", url: d.url || "", image: d.image || "",
        price: d.price || "", variant: d.variant || "",
      });
    }
    write(list);
    sync();
    if (panel && !panel.hidden) renderPanel();
    toast(on ? T.removedToast : T.savedToast);
  }

  const setAttr = (el, name, value) => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  };
  const setText = (el, value) => {
    if (el.textContent !== value) el.textContent = value;
  };

  function sync() {
    const list = read();
    const saved = new Set(list.map((i) => i.handle));
    for (const button of document.querySelectorAll("[data-wishlist-toggle]")) {
      const on = saved.has(button.dataset.handle);
      setAttr(button, "aria-pressed", on ? "true" : "false");
      const label = button.querySelector("[data-wishlist-label]");
      if (label) setText(label, on ? T.saved : T.add);
      else setAttr(button, "aria-label", `${on ? T.saved : T.add}: ${button.dataset.title || ""}`);
    }
    for (const count of document.querySelectorAll("[data-wishlist-count]")) {
      setText(count, String(list.length));
      if (count.hidden !== (list.length === 0)) count.hidden = list.length === 0;
    }
  }

  // --- wishlist panel -------------------------------------------------------------
  let panel = null;
  let panelList = null;
  let opener = null;

  function buildPanel() {
    panel = document.createElement("div");
    panel.className = "ns-panel";
    panel.hidden = true;
    panel.innerHTML =
      '<div class="ns-panel-backdrop" data-ns-close></div>' +
      '<aside class="ns-panel-box" role="dialog" aria-modal="true" aria-labelledby="ns-panel-title" tabindex="-1">' +
      '<header class="ns-panel-head"><h2 id="ns-panel-title"></h2>' +
      '<button type="button" class="ns-panel-close" data-ns-close><span aria-hidden="true">&times;</span></button></header>' +
      '<ul class="ns-panel-list"></ul><p class="ns-panel-note"></p></aside>';
    panel.querySelector("#ns-panel-title").textContent = T.title;
    panel.querySelector(".ns-panel-close").setAttribute("aria-label", T.close);
    panel.querySelector(".ns-panel-note").textContent = T.deviceNote;
    panelList = panel.querySelector(".ns-panel-list");
    document.body.appendChild(panel);
  }

  function renderPanel() {
    const list = read();
    panelList.replaceChildren();
    if (!list.length) {
      const empty = document.createElement("li");
      empty.className = "ns-empty";
      empty.textContent = T.empty;
      panelList.append(empty);
      return;
    }
    for (const item of list) {
      const li = document.createElement("li");
      li.className = "ns-item";

      const pic = document.createElement("a");
      pic.className = "ns-item-pic";
      pic.href = safeUrl(item.url);
      pic.tabIndex = -1;
      pic.setAttribute("aria-hidden", "true");
      const src = safeImg(item.image);
      if (src) {
        const img = document.createElement("img");
        img.src = src;
        img.alt = "";
        img.loading = "lazy";
        pic.append(img);
      }

      const copy = document.createElement("div");
      copy.className = "ns-item-copy";
      const name = document.createElement("a");
      name.className = "ns-item-title";
      name.href = safeUrl(item.url);
      name.textContent = item.title;
      const price = document.createElement("p");
      price.className = "ns-item-price";
      price.textContent = item.price || T.priceTbc;

      const actions = document.createElement("div");
      actions.className = "ns-item-actions";
      if (/^\d+$/.test(item.variant || "")) {
        const form = document.createElement("form");
        form.method = "post";
        form.action = "/cart/add";
        form.dataset.quickAdd = "";
        form.innerHTML = '<input type="hidden" name="quantity" value="1"><button type="submit" class="ns-card-btn"></button>';
        const id = document.createElement("input");
        id.type = "hidden";
        id.name = "id";
        id.value = item.variant;
        form.prepend(id);
        form.querySelector("button").textContent = T.addToCart;
        actions.append(form);
      } else {
        const choose = document.createElement("a");
        choose.className = "ns-card-btn";
        choose.href = safeUrl(item.url);
        choose.textContent = T.choose;
        actions.append(choose);
      }
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "ns-remove";
      remove.dataset.nsRemove = item.handle;
      remove.textContent = T.remove;
      actions.append(remove);

      copy.append(name, price, actions);
      li.append(pic, copy);
      panelList.append(li);
    }
  }

  function onPanelKey(event) {
    if (event.key === "Escape") {
      closePanel();
      return;
    }
    if (event.key !== "Tab") return;
    const items = [...panel.querySelectorAll("a[href], button, input:not([type='hidden'])")].filter((el) => el.offsetParent);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openPanel() {
    if (!panel) buildPanel();
    opener = document.activeElement;
    renderPanel();
    panel.hidden = false;
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("keydown", onPanelKey);
    panel.querySelector(".ns-panel-box").focus();
  }

  function closePanel() {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    document.documentElement.style.overflow = "";
    document.removeEventListener("keydown", onPanelKey);
    opener?.focus?.();
  }

  // --- quick add ------------------------------------------------------------------
  async function quickAdd(form) {
    const button = form.querySelector("button[type='submit']");
    const label = button ? button.textContent : "";
    if (button) button.disabled = true;
    try {
      const added = await fetch("/cart/add.js", {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (!added.ok) throw new Error(`HTTP ${added.status}`);
      const cart = await fetch("/cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
      for (const node of document.querySelectorAll("[data-cart-count]")) node.textContent = cart.item_count;
      if (button) button.textContent = T.added;
      toast(T.added);
      setTimeout(() => {
        if (!button) return;
        button.textContent = label;
        button.disabled = false;
      }, 1600);
    } catch (error) {
      // Never swallow a sale: fall back to a normal post, which lands on /cart.
      if (button) button.disabled = false;
      form.submit();
    }
  }

  // --- colour dots on product cards -----------------------------------------------
  function paintDots() {
    for (const row of document.querySelectorAll("[data-ns-dots]:not([data-painted])")) {
      row.dataset.painted = "1";
      const dots = [...row.querySelectorAll(".ns-dot")];
      const fills = dots.map((dot) => swatchFor(dot.dataset.colour));
      if (fills.some((f) => !f)) {
        // A colour we cannot draw: say how many instead of guessing a fill.
        const count = document.createElement("span");
        count.className = "ns-dots-count";
        count.textContent = `${row.dataset.count || dots.length} ${T.colours}`;
        for (const dot of dots) dot.remove();
        row.querySelector(".ns-dots-more")?.remove();
        row.prepend(count);
        continue;
      }
      dots.forEach((dot, i) => dot.style.setProperty("--sw", fills[i]));
    }
  }

  // --- cart page steppers ---------------------------------------------------------
  function cartSteppers() {
    const form = document.querySelector(".cart-page form.checkout-panel");
    if (!form) return;
    const update = form.querySelector("[name='update']");
    for (const input of form.querySelectorAll("input[name='updates[]']")) {
      if (input.closest(".ns-qty")) continue;
      const wrap = document.createElement("div");
      wrap.className = "ns-qty";
      input.before(wrap);
      const make = (text, label, delta) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = text;
        b.setAttribute("aria-label", label);
        b.addEventListener("click", () => {
          input.value = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
          if (form.requestSubmit && update) form.requestSubmit(update);
          else if (update) update.click();
        });
        return b;
      };
      wrap.append(make("−", T.decrease, -1), input, make("+", T.increase, 1));
    }
  }

  // --- wiring ---------------------------------------------------------------------
  document.addEventListener("click", (event) => {
    const heart = event.target.closest("[data-wishlist-toggle]");
    if (heart) {
      event.preventDefault();
      toggle(heart);
      return;
    }
    const open = event.target.closest("[data-wishlist-open]");
    if (open) {
      event.preventDefault();
      openPanel();
      return;
    }
    if (event.target.closest("[data-ns-close]")) {
      closePanel();
      return;
    }
    const remove = event.target.closest("[data-ns-remove]");
    if (remove) {
      write(read().filter((i) => i.handle !== remove.dataset.nsRemove));
      renderPanel();
      sync();
      toast(T.removedToast);
    }
  });

  document.addEventListener("submit", (event) => {
    const form = event.target.closest("form[data-quick-add]");
    if (!form) return;
    event.preventDefault();
    quickAdd(form);
  });

  // Another tab changed the list.
  window.addEventListener("storage", (event) => {
    if (event.key !== KEY) return;
    sync();
    if (panel && !panel.hidden) renderPanel();
  });

  // Collection filters swap the product grid in place, so new cards need their
  // hearts and dots set. The observer ignores the panel and only reacts to
  // nodes being added, and sync() only writes when a value actually changes,
  // so it cannot feed itself.
  let queued = 0;
  new MutationObserver((records) => {
    const relevant = records.some(
      (r) => r.addedNodes.length && !(panel && panel.contains(r.target)) && !(toastEl && toastEl.contains(r.target)),
    );
    if (!relevant || queued) return;
    queued = requestAnimationFrame(() => {
      queued = 0;
      sync();
      paintDots();
    });
  }).observe(document.body, { childList: true, subtree: true });

  sync();
  paintDots();
  cartSteppers();
})();

// --- rail arrows ---------------------------------------------------------------
// Product rows and the Shop-by-sport tiles scroll sideways. A trackpad or a
// thumb does that on its own; a mouse wheel does not, so desktop gets the two
// round arrows Nike puts beside a row's heading. CSS hides them on phones and
// whenever a row fits without scrolling.
(() => {
  if (window.novaRails) return;
  window.novaRails = true;
  const fr = (document.documentElement.lang || "").toLowerCase().startsWith("fr");
  const LABEL = fr ? { prev: "Précédent", next: "Suivant" } : { prev: "Previous", next: "Next" };
  const ICON = (d) =>
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="' + d + '"/></svg>';

  function bind(rail) {
    if (rail.dataset.nsRail) return;
    rail.dataset.nsRail = "1";
    let head = rail.previousElementSibling;
    if (!head) return;
    if (head.tagName === "H2") {
      const wrap = document.createElement("div");
      wrap.className = "ns-rail-head";
      head.before(wrap);
      wrap.append(head);
      head = wrap;
    }
    const nav = document.createElement("div");
    nav.className = "ns-rail-nav";
    nav.innerHTML =
      '<button type="button" class="ns-rail-btn" data-dir="-1" aria-label="' + LABEL.prev + '">' + ICON("M15 5l-7 7 7 7") + "</button>" +
      '<button type="button" class="ns-rail-btn" data-dir="1" aria-label="' + LABEL.next + '">' + ICON("M9 5l7 7-7 7") + "</button>";
    head.append(nav);
    const [prev, next] = nav.querySelectorAll("button");
    // Scroll snapping can park the first card up to the row's side padding in
    // from zero, so anything inside that padding still counts as the start.
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      const pad = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
      nav.hidden = max < 8;
      prev.disabled = rail.scrollLeft <= pad + 4;
      next.disabled = rail.scrollLeft >= max - 4;
    };
    nav.addEventListener("click", (event) => {
      const btn = event.target.closest("button");
      if (!btn) return;
      rail.scrollBy({ left: Number(btn.dataset.dir) * rail.clientWidth * 0.9, behavior: "smooth" });
    });
    rail.addEventListener("scroll", () => requestAnimationFrame(update), { passive: true });
    addEventListener("resize", update, { passive: true });
    update();
  }

  const scan = () => document.querySelectorAll(".product-rail, .sport-grid, .related-products .product-grid").forEach(bind);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scan);
  else scan();
  document.addEventListener("shopify:section:load", scan);
})();